import { Component, type ErrorInfo, type ReactNode, useEffect, useState } from "react";
import { HomePage } from "./components/HomePage.tsx";
import { GamePage } from "./components/GamePage.tsx";
import { FriendInviteLanding } from "./components/FriendInviteLanding.tsx";
import { InstallGate } from "./components/InstallGate.tsx";
import { SWUpdatePrompt } from "./components/SWUpdatePrompt.tsx";
import { friendCodeFromInvite } from "./lib/friend-code.ts";
import { isIosDevice, isStandaloneApp } from "./lib/install.ts";

function currentGameId(): string | null {
  const match = window.location.pathname.match(/^\/game\/([A-Za-z0-9_-]{8,24})\/?$/);
  return match?.[1] ?? null;
}

function currentFriendCode(): string | null {
  if (!/^\/friend\//.test(window.location.pathname)) return null;
  return friendCodeFromInvite(window.location.pathname, window.location.origin);
}

class ErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Application error", error, info);
  }

  render() {
    if (this.state.failed) {
      return (
        <main className="centered-page">
          <section className="error-card">
            <div className="brand-mark" aria-hidden="true">♞</div>
            <h1>Что-то пошло не так</h1>
            <p>Обновите страницу — ваше место в партии сохранено на этом устройстве.</p>
            <button className="button button-primary" onClick={() => window.location.reload()}>
              Обновить
            </button>
          </section>
        </main>
      );
    }
    return this.props.children;
  }
}

export function App() {
  const [gameId, setGameId] = useState(currentGameId);
  const [friendCode, setFriendCode] = useState(currentFriendCode);
  const [requiresIosInstall] = useState(() => isIosDevice() && !isStandaloneApp());

  useEffect(() => {
    const onPopState = () => {
      setGameId(currentGameId());
      setFriendCode(currentFriendCode());
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    if (!requiresIosInstall && friendCode && window.location.pathname.startsWith("/friend/")) {
      window.history.replaceState(null, "", "/");
    }
  }, [friendCode, requiresIosInstall]);

  let page: ReactNode;
  if (requiresIosInstall && friendCode) {
    page = <FriendInviteLanding code={friendCode} />;
  } else if (requiresIosInstall) {
    page = <InstallGate />;
  } else {
    page = gameId ? <GamePage gameId={gameId} /> : <HomePage initialFriendCode={friendCode} />;
  }

  return (
    <>
      <ErrorBoundary>{page}</ErrorBoundary>
      <SWUpdatePrompt />
    </>
  );
}
